/**
 * Main Layout Component
 * Provides the overall application structure with navbar and sidebar
 * Header spans full width, sidebar below header
 */

import React, { useState } from 'react';
import { Alert, AlertTitle, Box, Button, CircularProgress, CssBaseline, Stack, Toolbar } from '@mui/material';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import useSidebarState from '../../hooks/useSidebarState';
import { useTenant } from '../../contexts/TenantContext';
import { usePermissions } from '../../contexts/PermissionContext';

const drawerWidth = 240;
const collapsedWidth = 64;

function MainLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const sidebarState = useSidebarState();
  const { loading: tenantLoading, tenant, error: tenantError, reload: reloadTenant } = useTenant();
  const { logout } = usePermissions();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const effectiveWidth = sidebarState.collapsed ? collapsedWidth : drawerWidth;

  // The tenant could not be resolved. Show it rather than rendering the app against
  // a guessed identity: the previous placeholder tenant made a real operating tenant
  // display as "Master" with an empty sidebar, while the API served the correct
  // tenant's data — a silent, near-undiagnosable split between the two layers.
  if (!tenantLoading && tenantError) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', p: 3 }}>
        <Alert severity="error" sx={{ maxWidth: 560 }}>
          <AlertTitle>Could not load your workspace</AlertTitle>
          We could not determine which organisation your account belongs to, so the
          application has not been loaded. No data has been changed. Please retry, and if
          the problem continues contact your system administrator.
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button variant="contained" size="small" onClick={reloadTenant}>Retry</Button>
            <Button variant="outlined" size="small" onClick={logout}>Sign out</Button>
          </Stack>
        </Alert>
      </Box>
    );
  }

  // Block render until TenantContext resolves so localStorage.selectedTenantId
  // is populated before any child useEffect fires its first apiGet — otherwise
  // the X-Tenant-ID header is missing and the API silently resolves to a
  // different tenant than the one the UI is about to display.
  if (tenantLoading || !tenant?.id) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
        <CircularProgress />
        <div>Loading...</div>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <CssBaseline />

      {/* Header offsets to the right of the sidebar on desktop */}
      <Navbar handleDrawerToggle={handleDrawerToggle} sidebarWidth={effectiveWidth} />

      {/* Sidebar */}
      <Sidebar
        drawerWidth={drawerWidth}
        collapsedWidth={collapsedWidth}
        mobileOpen={mobileOpen}
        handleDrawerToggle={handleDrawerToggle}
        sidebarState={sidebarState}
      />

      {/* Main content area */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${effectiveWidth}px)` },
          minHeight: '100vh',
          backgroundColor: (theme) =>
            theme.palette.mode === 'light'
              ? theme.palette.grey[100]
              : theme.palette.grey[900],
          transition: (theme) =>
            theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}

export default MainLayout;
